import React, { useMemo } from 'react';
import { CustomStepProps } from '../../types/survey';
import { Button } from '../ui/Button';

/**
 * BMI Calculation Screen
 * Displays BMI results and health insights based on weight and height data
 */
export const BMICalculationScreen: React.FC<CustomStepProps> = ({ 
  onNext, 
  onBack,
  surveyData 
}) => {
  // Calculate BMI from survey data
  const bmiData = useMemo(() => {
    const weight = surveyData?.answers?.weight;
    const height = surveyData?.answers?.height;
    const gender = surveyData?.answers?.gender;
    const age = surveyData?.answers?.chronologicalAge;
    
    if (!weight || !height) {
      return null;
    }
    
    // Convert height from cm to meters and calculate BMI
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);
    
    // Get gender display text
    const genderText = gender === 'Item 1' ? 'Female' : 
                      gender === 'Item 2' ? 'Male' : 
                      gender === 'Item 3' ? 'Other' : 'Not specified';
    
    // Age-adjusted BMI interpretation
    let ageNote = '';
    if (age < 20) {
      ageNote = 'Note: BMI interpretation for children and teenagers requires percentile charts specific to age and sex.';
    } else if (age >= 65) {
      ageNote = 'Note: For adults over 65, slightly higher BMI (23-30) may be protective against frailty.';
    }
    
    // Gender-specific considerations
    let genderNote = '';
    if (genderText === 'Female') {
      genderNote = 'Women typically have more body fat than men at the same BMI. Consider body composition analysis for accurate assessment.';
    } else if (genderText === 'Male') {
      genderNote = 'Men typically have more muscle mass than women at the same BMI. Athletes may have high BMI despite low body fat.';
    }
    
    // Longevity Expert Recommendations based on BMI
    let expertRecommendation = '';
    if (bmi < 18.5) {
      expertRecommendation = `Based on your BMI of ${bmi.toFixed(1)}, our longevity experts recommend focusing on nutrient-dense foods and strength training. Building healthy muscle mass is crucial for metabolic health and longevity. Consider working with a nutritionist to ensure adequate protein intake (1.2-1.5g per kg body weight) and incorporating resistance exercises 3-4 times per week. Regular monitoring of bone density and vitamin D levels is also advised.`;
    } else if (bmi >= 18.5 && bmi <= 25.0) {
      expertRecommendation = `Excellent! Your BMI of ${bmi.toFixed(1)} is in the optimal range for longevity. Our experts recommend maintaining this healthy weight through consistent habits: Mediterranean-style diet rich in omega-3s, 150 minutes of moderate exercise weekly, strength training twice a week, and prioritizing 7-9 hours of quality sleep. Focus on building lean muscle mass and maintaining metabolic flexibility through varied physical activities.`;
    } else {
      expertRecommendation = `With a BMI of ${bmi.toFixed(1)}, our longevity experts recommend a gradual, sustainable approach to optimize your healthspan. Focus on anti-inflammatory foods, intermittent fasting (12-16 hour fasting window), and low-impact exercises like swimming or cycling. Aim for a 5-10% weight reduction initially, which can significantly improve metabolic markers. Consider continuous glucose monitoring to understand your body's response to different foods.`;
    }
    
    // Determine BMI category with age considerations
    let category = '';
    let categoryColor = '';
    let healthRisk = '';
    
    // Adjusted thresholds for older adults
    const underweightThreshold = age >= 65 ? 23 : 18.5;
    const overweightThreshold = age >= 65 ? 30 : 25;
    
    if (bmi < underweightThreshold) {
      category = 'Underweight';
      categoryColor = 'text-blue-600';
      healthRisk = age >= 65 
        ? 'May increase risk of frailty and bone loss in older adults'
        : 'May indicate nutritional deficiency or underlying health conditions';
    } else if (bmi >= underweightThreshold && bmi < overweightThreshold) {
      category = 'Normal weight';
      categoryColor = 'text-green-600';
      healthRisk = age >= 65
        ? 'Healthy range for older adults'
        : 'Optimal range for most adults';
    } else if (bmi >= overweightThreshold && bmi < 30) {
      category = 'Overweight';
      categoryColor = 'text-yellow-600';
      healthRisk = age >= 65
        ? 'May be protective in older adults, but monitor cardiovascular health'
        : 'May increase risk of health problems';
    } else if (bmi >= 30 && bmi < 35) {
      category = 'Obese (Class I)';
      categoryColor = 'text-orange-600';
      healthRisk = 'Significantly increased health risks';
    } else if (bmi >= 35 && bmi < 40) {
      category = 'Obese (Class II)';
      categoryColor = 'text-red-600';
      healthRisk = 'High health risks, medical supervision recommended';
    } else {
      category = 'Obese (Class III)';
      categoryColor = 'text-red-800';
      healthRisk = 'Very high health risks, immediate medical attention advised';
    }
    
    return {
      weight,
      height,
      bmi: bmi.toFixed(1),
      category,
      categoryColor,
      healthRisk,
      gender: genderText,
      age,
      ageNote,
      genderNote,
      expertRecommendation
    };
  }, [surveyData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-gold-lightest to-royal-blue-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Your Personalized BMI Results
            </h1>
            <p className="text-lg text-gray-600">
              Customized analysis based on your age, gender, and body measurements
            </p>
          </div>

          {bmiData ? (
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Age</h3>
                  <p className="text-xl font-bold text-gray-900">{bmiData.age} years</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Gender</h3>
                  <p className="text-xl font-bold text-gray-900">{bmiData.gender}</p>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4 text-center">
                  <h3 className="text-sm font-semibold text-royal-gold mb-1">Weight</h3>
                  <p className="text-xl font-bold text-gray-900">{bmiData.weight} kg</p>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4 text-center">
                  <h3 className="text-sm font-semibold text-royal-blue mb-1">Height</h3>
                  <p className="text-xl font-bold text-gray-900">{bmiData.height} cm</p>
                </div>
              </div>

              {/* BMI Result */}
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue rounded-lg p-8 text-center text-white">
                <h2 className="text-2xl font-semibold mb-2">Your BMI</h2>
                <div className="text-6xl font-bold mb-4">{bmiData.bmi}</div>
                <p className="text-xl opacity-90">{bmiData.category}</p>
              </div>

              {/* BMI Category Details */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  BMI Category: <span className={bmiData.categoryColor}>{bmiData.category}</span>
                </h3>
                <p className="text-gray-700 text-center mb-4">{bmiData.healthRisk}</p>
                
                {/* BMI Scale */}
                <div className="mt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3 text-center">BMI Scale Reference</h4>
                  <div className="grid grid-cols-5 gap-2 text-xs">
                    <div className="text-center">
                      <div className="w-full h-3 bg-blue-400 rounded-t"></div>
                      <p className="mt-1">Underweight</p>
                      <p className="font-medium">&lt;18.5</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-3 bg-green-400 rounded-t"></div>
                      <p className="mt-1">Normal</p>
                      <p className="font-medium">18.5-24.9</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-3 bg-yellow-400 rounded-t"></div>
                      <p className="mt-1">Overweight</p>
                      <p className="font-medium">25-29.9</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-3 bg-orange-400 rounded-t"></div>
                      <p className="mt-1">Obese I</p>
                      <p className="font-medium">30-34.9</p>
                    </div>
                    <div className="text-center">
                      <div className="w-full h-3 bg-red-400 rounded-t"></div>
                      <p className="mt-1">Obese II+</p>
                      <p className="font-medium">≥35</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personalized Health Insights */}
              <div className="bg-royal-gold-lightest rounded-lg p-6">
                <h3 className="text-xl font-semibold text-royal-gold mb-4 text-center">
                  💡 Personalized Health Insights
                </h3>
                <div className="space-y-4">
                  {/* Longevity Expert Recommendations - Featured at top */}
                  <div className="bg-gradient-to-r from-royal-gold to-royal-blue bg-opacity-10 rounded-lg p-5 border-2 border-royal-gold">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center text-lg">
                      <span className="text-xl mr-2">🎯</span>
                      Our Longevity Experts Recommend
                    </h4>
                    <p className="text-gray-800 leading-relaxed">{bmiData.expertRecommendation}</p>
                  </div>
                  
                  {/* Age-specific note */}
                  {bmiData.ageNote && (
                    <div className="bg-white bg-opacity-70 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <span className="text-lg mr-2">📊</span>
                        Age Consideration
                      </h4>
                      <p className="text-gray-700 text-sm">{bmiData.ageNote}</p>
                    </div>
                  )}
                  
                  {/* Gender-specific note */}
                  {bmiData.genderNote && (
                    <div className="bg-white bg-opacity-70 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <span className="text-lg mr-2">👤</span>
                        Gender Consideration
                      </h4>
                      <p className="text-gray-700 text-sm">{bmiData.genderNote}</p>
                    </div>
                  )}
                  
                  {/* General BMI information */}
                  <div className="bg-white bg-opacity-70 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="text-lg mr-2">ℹ️</span>
                      General Information
                    </h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• BMI is a screening tool, not a diagnostic measure</li>
                      <li>• Consider waist circumference and body composition for complete assessment</li>
                      <li>• Regular physical activity and balanced nutrition are key to health</li>
                      <li>• Consult healthcare professionals for personalized advice</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Missing Data
              </h3>
              <p className="text-gray-600">
                We couldn't calculate your BMI because weight or height data is missing.
                Please go back and complete the previous questions.
              </p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button
              onClick={onBack}
              variant="secondary"
              size="lg"
            >
              Previous
            </Button>
            
            <Button
              onClick={onNext}
              variant="primary"
              size="lg"
              disabled={!bmiData}
            >
              Continue
            </Button>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              This BMI calculation is for informational purposes only and should not replace professional medical advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};